import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface LeagueExtraSlotsProps {
  leagueId: string;
  matchDurationMinutes: number;
  autoReleaseHours: number;
}

export default function LeagueExtraSlots({ leagueId, matchDurationMinutes, autoReleaseHours }: LeagueExtraSlotsProps) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { courts: orgCourts } = useOrgHall();
  const courts = useMemo(() => ((orgCourts ?? []) as any[]).filter((c) => c.status === "active"), [orgCourts]);

  const [newSlot, setNewSlot] = useState({ court_id: "", slot_date: "", slot_time: "" });

  const { data: slots = [] } = useQuery({
    queryKey: ["league-extra-slots", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_available_slots")
        .select("*")
        .eq("league_id", leagueId)
        .order("slot_date")
        .order("slot_time");
      if (error) throw error;
      return data ?? [];
    },
  });

  const addSlot = useMutation({
    mutationFn: async () => {
      const slotDateTime = new Date(`${newSlot.slot_date}T${newSlot.slot_time}`);
      const autoReleaseAt = new Date(slotDateTime.getTime() - autoReleaseHours * 60 * 60 * 1000);

      const { error } = await supabase.from("league_available_slots").insert({
        league_id: leagueId,
        court_id: newSlot.court_id,
        slot_date: newSlot.slot_date,
        slot_time: newSlot.slot_time,
        duration_minutes: matchDurationMinutes,
        status: "available",
        auto_release_at: autoReleaseAt.toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-extra-slots", leagueId] });
      setNewSlot({ court_id: "", slot_date: "", slot_time: "" });
      toast.success(t("admin:toast.extraSlotAdded"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotAddExtraSlot")); },
  });

  const deleteSlot = useMutation({
    mutationFn: async (slotId: string) => {
      const { error } = await supabase.from("league_available_slots").delete().eq("id", slotId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-extra-slots", leagueId] });
      toast.success(t("admin:toast.extraSlotRemoved"));
    },
  });

  const statusLabel: Record<string, string> = {
    available: t("leagues.extraSlots.available"),
    booked: t("leagues.extraSlots.booked"),
    released: t("leagues.extraSlots.released"),
  };

  const statusColor: Record<string, string> = {
    available: "default",
    booked: "secondary",
    released: "outline",
  };

  const canAdd = newSlot.court_id && newSlot.slot_date && newSlot.slot_time;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">{t("leagues.extraSlots.addExtraTime")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">{t("leagues.extraSlots.court")}</Label>
              <Select value={newSlot.court_id} onValueChange={(v) => setNewSlot({ ...newSlot, court_id: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t("leagues.extraSlots.selectCourt")} /></SelectTrigger>
                <SelectContent>
                  {courts.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("leagues.extraSlots.date")}</Label>
              <Input type="date" className="h-8 text-xs" value={newSlot.slot_date} onChange={(e) => setNewSlot({ ...newSlot, slot_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("leagues.extraSlots.time")}</Label>
              <Input type="time" className="h-8 text-xs" value={newSlot.slot_time} onChange={(e) => setNewSlot({ ...newSlot, slot_time: e.target.value })} />
            </div>
            <div className="flex items-end">
              <Button size="sm" className="h-8 gap-1" disabled={!canAdd || addSlot.isPending} onClick={() => addSlot.mutate()}>
                <Plus className="h-3.5 w-3.5" /> {t("seasons.groups.add")}
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            {t("leagues.extraSlots.autoReleaseHint", { hours: autoReleaseHours })}
          </p>
        </CardContent>
      </Card>

      {slots.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">{t("leagues.extraSlots.existingSlots", { count: slots.length })}</p>
          <div className="space-y-1">
            {slots.map((slot: any) => {
              const courtName = courts.find((c: any) => c.id === slot.court_id)?.name ?? "?";
              return (
                <div key={slot.id} className="flex items-center justify-between gap-2 rounded border p-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{slot.slot_date}</span>
                    <span>{slot.slot_time}</span>
                    <span className="text-muted-foreground">{courtName}</span>
                    <Badge variant={statusColor[slot.status] as any} className="text-[10px]">
                      {statusLabel[slot.status] ?? slot.status}
                    </Badge>
                  </div>
                  {slot.status === "available" && (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteSlot.mutate(slot.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
